import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { ChatwootData, ChatwootContact, ChatwootAgent, ChatwootConversation } from '../types/chatwoot';
import { PostMessageBridge } from '../services/PostMessageBridge';
import type { AppContextPayload } from '../types/postMessage';
import { useTheme } from './ThemeContext';

const API_BASE_URL = '/api';

interface AutoProvisionResponse {
  success: boolean;
  data: {
    token: string;
    user: {
      id: string;
      nome: string;
      email: string;
      role: string;
      ativo: boolean;
      tenantId?: string | null;
      ultimoLogin?: string | null;
      criadoEm: string;
    };
    tenant: {
      id: string;
      slug: string;
      name: string;
      active: boolean;
      allowWhatsWeb: boolean;
      allowOfficialApi: boolean;
      platformId: string | null;
      platform: {
        id: string;
        name: string;
        value: string;
        url: string;
        active: boolean;
      } | null;
      plataforma: string | null;
      idPlataforma: string | null;
    };
    isSuperAdmin: boolean;
  };
}

interface ChatwootContextType {
  // Estado
  chatwootData: ChatwootData | null;
  isInChatwoot: boolean;
  isLoading: boolean;
  
  // Dados específicos
  currentContact: ChatwootContact | null;
  currentAgent: ChatwootAgent | null;
  conversation: ChatwootConversation | null;
  
  // Contexto global (Sidebar)
  parentTheme: 'light' | 'dark' | null;
  accountId: number | null;
  accountName: string | null;
  locale: string | null;
  teams: any[];
  
  // Auth via auto-provision
  token: string | null;
  user: AutoProvisionResponse['data']['user'] | null;
  tenant: AutoProvisionResponse['data']['tenant'] | null;
  isSuperAdmin: boolean;
  isAuthenticated: boolean;
  isAuthReady: boolean;
  
  // Ações
  refreshData: () => void;
  sendToParent: (event: string, data?: any) => void;
  isDataReady: boolean;
  reProvision: () => void;
}

const ChatwootContext = createContext<ChatwootContextType | undefined>(undefined);

interface ChatwootProviderProps {
  children: ReactNode;
}

export const ChatwootProvider: React.FC<ChatwootProviderProps> = ({ children }) => {
  const { setTheme } = useTheme();
  const [chatwootData, setChatwootData] = useState<ChatwootData | null>(null);
  const [isInChatwoot, setIsInChatwoot] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [parentTheme, setParentTheme] = useState<'light' | 'dark' | null>(null);
  const [accountId, setAccountId] = useState<number | null>(null);
  const [accountName, setAccountName] = useState<string | null>(null);
  const [locale, setLocale] = useState<string | null>(null);
  const [teams, setTeams] = useState<any[]>([]);

  // Auth state
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<AutoProvisionResponse['data']['user'] | null>(null);
  const [tenant, setTenant] = useState<AutoProvisionResponse['data']['tenant'] | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isAuthReady, setIsAuthReady] = useState(false);
  
  // Ref para evitar chamadas duplicadas ao auto-provision
  const provisioningRef = useRef(false);
  // Ref para armazenar os últimos dados do appContext para re-provision
  const lastAppContextRef = useRef<AppContextPayload | null>(null);
  // Ref para debounce de re-provision (evitar loop infinito no 401)
  const reProvisionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastReProvisionRef = useRef<number>(0);
  const RE_PROVISION_COOLDOWN_MS = 5000; // 5 segundos de cooldown

  const isAuthenticated = !!user && !!token;

  /**
   * Chamar auto-provision no backend
   */
  const callAutoProvision = useCallback(async (data: AppContextPayload): Promise<boolean> => {
    // Evitar chamadas duplicadas
    if (provisioningRef.current) {
      // console.log('🔄 [Chatwoot] Auto-provision já em andamento, ignorando...');
      return false;
    }

    provisioningRef.current = true;

    try {
      // console.log('🔐 [Chatwoot] Chamando auto-provision...');

      const response = await fetch(`${API_BASE_URL}/auth/auto-provision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          account_id: data.account_id,
          account_name: data.account_name,
          installation_url: data.installation_url,
          agent_email: data.currentAgent?.email,
          agent_name: data.currentAgent?.name,
          userType: data.userType,
          role: data.role
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // console.error('❌ [Chatwoot] Auto-provision falhou:', errorData);
        return false;
      }

      const result: AutoProvisionResponse = await response.json();

      if (result.success && result.data) {
        setToken(result.data.token);
        
        // SuperAdmin mode is only activated if the URL path starts with /superadmin
        // When NOT on /superadmin, override the user role to 'USER' so the SuperAdmin
        // sees exactly the same UI as a regular client user (no config, no users tab, etc.)
        const isSuperAdminUrl = window.location.pathname.startsWith('/superadmin');
        const isSuperAdminUser = result.data.isSuperAdmin && result.data.user.role === 'SUPERADMIN';
        
        if (isSuperAdminUser && !isSuperAdminUrl) {
          // Downgrade completo: role + tenantId da conta corrente
          // Assim o TenantContext e API usam o tenant correto
          setUser({
            ...result.data.user,
            role: 'USER',
            tenantId: result.data.tenant.id
          });
          // Forçar localStorage com o tenant da conta para que API envie X-Tenant-Id
          localStorage.setItem('selected_tenant_id', result.data.tenant.id);
        } else {
          setUser(result.data.user);
        }
        
        setTenant(result.data.tenant);
        setIsSuperAdmin(result.data.isSuperAdmin && isSuperAdminUrl);
        setIsAuthReady(true);

        // Salvar token para uso nas chamadas à API
        localStorage.setItem('auth_token', result.data.token);
        localStorage.setItem('auth_level', 'iframe');
        localStorage.setItem('url_login', 'true');

        // console.log('✅ [Chatwoot] Auto-provision concluído:', {
        //   userId: result.data.user.id,
        //   email: result.data.user.email,
        //   role: result.data.user.role,
        //   tenantName: result.data.tenant.name,
        //   isSuperAdmin: result.data.isSuperAdmin
        // });

        return true;
      }

      return false;
    } catch (error) {
      // console.error('❌ [Chatwoot] Erro no auto-provision:', error);
      setIsAuthReady(true); // Marcar como ready para mostrar tela de erro (não loading infinito)
      return false;
    } finally {
      provisioningRef.current = false;
    }
  }, []);

  /**
   * Re-provision (chamado pelo interceptor 401)
   */
  const reProvision = useCallback(() => {
    // Debounce: evitar loop infinito de re-provision (se auto-provision retorna 401)
    const now = Date.now();
    if (now - lastReProvisionRef.current < RE_PROVISION_COOLDOWN_MS) {
      // console.warn('⚠️ [Chatwoot] Re-provision em cooldown, ignorando...');
      return;
    }
    lastReProvisionRef.current = now;

    if (lastAppContextRef.current) {
      // console.log('🔄 [Chatwoot] Re-provisioning após 401...');
      callAutoProvision(lastAppContextRef.current);
    } else if (isInChatwoot) {
      // console.log('🔄 [Chatwoot] Solicitando novo appContext para re-provision...');
      PostMessageBridge.requestAppContext();
    }
  }, [isInChatwoot, callAutoProvision]);

  useEffect(() => {
    // Detectar se está dentro de um iframe
    const isIframe = PostMessageBridge.isInsideIframe;
    setIsInChatwoot(isIframe);

    if (!isIframe) {
      setIsLoading(false);
      setIsAuthReady(true); // Marcar como ready (sem auth) em modo standalone
      // console.log('📱 [Chatwoot] Sistema rodando em modo standalone (não está no Chatwoot)');
      return;
    }

    // console.log('📱 [Chatwoot] Sistema detectado dentro de iframe — Modo Chatwoot ativado');

    // Inicializar o bridge (idempotente)
    PostMessageBridge.init();

    // Registrar handler para appContext via bridge
    const unsubscribe = PostMessageBridge.on('appContext', async (data: AppContextPayload) => {
      // console.log('📦 [Chatwoot] appContext recebido via Bridge:', {
      //   account_id: data.account_id,
      //   agent_email: data.currentAgent?.email,
      //   agent_name: data.currentAgent?.name,
      //   userType: data.userType,
      //   theme: data.theme
      // });

      // Salvar referência para re-provision
      lastAppContextRef.current = data;

      // Mapear AppContextPayload → ChatwootData
      const mapped: ChatwootData = {
        account_id: data.account_id,
        account_name: data.account_name,
        installation_url: data.installation_url,
        locale: data.locale,
        theme: data.theme,
        role: data.role,
        userType: data.userType,
        teams: data.teams,
        currentAgent: data.currentAgent,
        conversation: data.conversation as ChatwootConversation | undefined,
        contact: data.contact as ChatwootContact | undefined,
      };

      setChatwootData(mapped);
      setParentTheme(data.theme || null);

      // Sincronizar tema com o app pai em tempo real
      if (data.theme === 'light' || data.theme === 'dark') {
        setTheme(data.theme);
      }
      setAccountId(data.account_id || null);
      setAccountName(data.account_name || null);
      setLocale(data.locale || null);
      setTeams(data.teams || []);
      setIsLoading(false);

      // Auto-provision: chamar backend para criar/encontrar conta
      if (data.account_id && data.currentAgent?.email) {
        await callAutoProvision(data);
      } else {
        // Dados insuficientes para auto-provision — marcar como ready
        // console.warn('⚠️ [Chatwoot] appContext sem account_id ou email, não é possível auto-provision');
        setIsAuthReady(true);
      }
    });

    // Timeout para caso não receba resposta da plataforma
    // Importante: setar isAuthReady=true também, para evitar loading infinito
    // quando a plataforma falha ao enviar appContext (ex: erro em dashboardApps.js)
    const timeout = setTimeout(() => {
      setIsLoading(prev => {
        if (prev) {
          // console.warn('⏱️ [Chatwoot] Timeout aguardando dados da plataforma (8 segundos)');
          setIsAuthReady(true); // ← evita loading infinito se a plataforma falhou
          return false;
        }
        return prev;
      });
    }, 8000);

    return () => {
      unsubscribe();
      clearTimeout(timeout);
      if (reProvisionTimerRef.current) {
        clearTimeout(reProvisionTimerRef.current);
      }
    };
  }, [callAutoProvision, setTheme]);

  /**
   * Solicita atualização dos dados ao app pai
   */
  const refreshData = useCallback(() => {
    if (!isInChatwoot) return;
    // console.log('🔄 [Chatwoot] Solicitando refresh dos dados ao pai');
    PostMessageBridge.requestAppContext();
  }, [isInChatwoot]);

  /**
   * Envia mensagem genérica ao app pai
   */
  const sendToParent = useCallback((event: string, data?: any) => {
    if (!isInChatwoot) {
      // console.warn('⚠️ [Chatwoot] sendToParent ignorado — não está no Chatwoot');
      return;
    }
    PostMessageBridge.send(event, data);
  }, [isInChatwoot]);

  const value: ChatwootContextType = {
    chatwootData,
    isInChatwoot,
    isLoading,
    currentContact: (chatwootData?.contact as ChatwootContact) || null,
    currentAgent: chatwootData?.currentAgent || null,
    conversation: (chatwootData?.conversation as ChatwootConversation) || null,
    parentTheme,
    accountId,
    accountName,
    locale,
    teams,
    // Auth
    token,
    user,
    tenant,
    isSuperAdmin,
    isAuthenticated,
    isAuthReady,
    // Ações
    refreshData,
    sendToParent,
    isDataReady: !isLoading && (chatwootData !== null || !isInChatwoot),
    reProvision,
  };

  return (
    <ChatwootContext.Provider value={value}>
      {children}
    </ChatwootContext.Provider>
  );
};

/**
 * Hook para consumir o contexto do Chatwoot
 */
export const useChatwoot = (): ChatwootContextType => {
  const context = useContext(ChatwootContext);
  if (context === undefined) {
    throw new Error('useChatwoot must be used within a ChatwootProvider');
  }
  return context;
};
